import { CreditCardIcon } from "lucide-react"
import { cn } from "@/lib/utils"

export type PaymentMethod = "stripe" | "paypal"

interface PaymentMethodSelectorProps {
  /**
   * Selected payment method
   */
  value: PaymentMethod

  /**
   * Change handler
   */
  onChange: (method: PaymentMethod) => void

  /**
   * Additional CSS classes
   */
  className?: string
}

export function PaymentMethodSelector({
  value,
  onChange,
  className = "",
}: PaymentMethodSelectorProps) {
  return (
    <div className={cn("space-y-3", className)}>
      {/* Stripe Option */}
      <label
        className={cn(
          "flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all",
          value === "stripe"
            ? "border-primary bg-primary/5"
            : "border-border bg-card hover:border-primary/50"
        )}
      >
        <input
          type="radio"
          name="payment-method"
          value="stripe"
          checked={value === "stripe"}
          onChange={() => onChange("stripe")}
          className="h-4 w-4 text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground">Credit/Debit Card</span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Pay securely with your card
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Card Icons */}
          <div className="flex items-center gap-1">
            <div className="h-6 w-9 rounded bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center">
              <span className="text-[8px] font-bold text-white">VISA</span>
            </div>
            <div className="h-6 w-9 rounded bg-gradient-to-br from-red-600 to-orange-600 flex items-center justify-center">
              <div className="flex gap-[2px]">
                <div className="h-3 w-3 rounded-full bg-red-500" />
                <div className="h-3 w-3 rounded-full bg-orange-400 -ml-2" />
              </div>
            </div>
            <div className="h-6 w-9 rounded bg-gradient-to-br from-blue-700 to-blue-900 flex items-center justify-center">
              <span className="text-[7px] font-bold text-white">AMEX</span>
            </div>
          </div>
        </div>
      </label>

      {/* PayPal Option */}
      <label
        className={cn(
          "flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all",
          value === "paypal"
            ? "border-primary bg-primary/5"
            : "border-border bg-card hover:border-primary/50"
        )}
      >
        <input
          type="radio"
          name="payment-method"
          value="paypal"
          checked={value === "paypal"}
          onChange={() => onChange("paypal")}
          className="h-4 w-4 text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2"
        />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground">PayPal</span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            Pay with your PayPal account
          </p>
        </div>
        <div className="h-6 px-3 rounded bg-[#0070ba] flex items-center justify-center">
          <span className="text-xs font-bold text-white">PayPal</span>
        </div>
      </label>
    </div>
  )
}